import { ui } from "./styles.js";
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Button, Card, EmptyState, Icon, SelectField, Status } from "@snowiespace/ui/web";
import { ProtocolError } from "@snowiespace/protocol";
import { client } from "./api.js";
import { Field, OperationError, object, stringValue, type JsonObject } from "./shared.js";

type Task = {
  id: string; name: string; target: { kind: string; id: string }; action: "snowie"; instruction: string;
  recurrence: { kind: "once" | "interval"; dueAt: string; everySeconds?: number };
  timeZone: string; state: "enabled" | "paused"; revision: number; nextDueAt?: string; createdAt: string; updatedAt: string;
};
type Fire = {
  id: string; taskId: string; taskRevision: number; triggerKind: "scheduled" | "manual"; dueAt: string;
  coalescedOccurrences: number; state: "claimed" | "dispatched" | "failed"; conversationId?: string; jobId?: string;
  jobState?: string; diagnosticCode?: string; createdAt: string; dispatchedAt?: string;
};
type TargetResource = { id: string; title: string; targetKind: string; pickerOperationId: string };
type IntervalUnit = "minute" | "hour" | "day" | "week";
const intervalSeconds: Record<IntervalUnit, number> = { minute: 60, hour: 3_600, day: 86_400, week: 604_800 };

function asTasks(value: unknown): Task[] {
  const rows = object(value).tasks;
  return Array.isArray(rows) ? rows as Task[] : [];
}

function asFires(value: unknown): Fire[] {
  const rows = object(value).fires;
  return Array.isArray(rows) ? rows as Fire[] : [];
}

function browserTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
}

function dateTimeParts(value: Date, timeZone: string): Record<string, string> {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone, year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit", hourCycle: "h23",
  }).formatToParts(value);
  return Object.fromEntries(parts.filter((part) => part.type !== "literal").map((part) => [part.type, part.value]));
}

function localInput(value: string, timeZone: string): string {
  const parts = dateTimeParts(new Date(value), timeZone);
  return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`;
}

function zonedISOString(value: string, timeZone: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(value);
  if (!match) throw new Error("Due time must be a complete local date and time.");
  const [, year, month, day, hour, minute] = match;
  const wallClock = Date.UTC(Number(year), Number(month) - 1, Number(day), Number(hour), Number(minute));
  let instant = wallClock;
  for (let attempt = 0; attempt < 3; attempt += 1) {
    const parts = dateTimeParts(new Date(instant), timeZone);
    const represented = Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day), Number(parts.hour), Number(parts.minute));
    instant += wallClock - represented;
  }
  const result = new Date(instant);
  if (localInput(result.toISOString(), timeZone) !== value) throw new Error("That local time does not exist in the selected time zone.");
  return result.toISOString();
}

function dueLabel(value?: string): string {
  return value ? new Date(value).toLocaleString() : "No next due occurrence";
}

function intervalParts(value = 3_600): { unit: IntervalUnit; value: string } {
  for (const unit of ["week", "day", "hour", "minute"] as const) {
    if (value >= intervalSeconds[unit] && value % intervalSeconds[unit] === 0) return { unit, value: String(value / intervalSeconds[unit]) };
  }
  return { unit: "minute", value: String(Math.max(1, Math.round(value / 60))) };
}

function repeatLabel(value: string, unit: IntervalUnit): string {
  const count = Number(value);
  return `Every ${Number.isFinite(count) && count > 0 ? count : "—"} ${unit}${count === 1 ? "" : "s"}`;
}

export function ScheduleView({ spaceId, moduleId, title, configuration }: { spaceId: string; moduleId: string; title: string; configuration: JsonObject }) {
  const queryClient = useQueryClient();
  const operation = (name: string): string => stringValue(configuration[name]) ?? `schedule.${name.replace("Operation", "")}`;
  const bootstrap = useQuery({ queryKey: ["bootstrap", spaceId], queryFn: () => client.bootstrap(spaceId) });
  const resources = useMemo<TargetResource[]>(() => (bootstrap.data?.modules ?? []).flatMap((item) => {
    const pickerTargets = item.targets.filter((target) => target.pickerOperationId);
    return pickerTargets.map((target) => ({
      id: `${item.id}:${target.kind}`,
      title: pickerTargets.length === 1 ? item.title : `${item.title} · ${target.kind}`,
      targetKind: target.kind,
      pickerOperationId: target.pickerOperationId!,
    }));
  }), [bootstrap.data]);
  const tasks = useQuery({
    queryKey: ["schedule", "tasks", spaceId, moduleId],
    queryFn: async () => asTasks((await client.invoke(spaceId, operation("listOperation"), {}, false)).output),
    refetchInterval: 5_000,
  });
  const [selectedId, setSelectedId] = useState<string>();
  const [isCreating, setIsCreating] = useState(false);
  const [mobilePane, setMobilePane] = useState<"list" | "editor">("list");
  const [name, setName] = useState("");
  const [instruction, setInstruction] = useState("");
  const [resourceId, setResourceId] = useState("");
  const [targetId, setTargetId] = useState("");
  const [recurrenceKind, setRecurrenceKind] = useState<"once" | "interval">("once");
  const [dueAt, setDueAt] = useState(() => localInput(new Date(Date.now() + 3_600_000).toISOString(), browserTimeZone()));
  const [intervalValue, setIntervalValue] = useState("1");
  const [intervalUnit, setIntervalUnit] = useState<IntervalUnit>("hour");
  const [timeZone, setTimeZone] = useState(browserTimeZone);
  const [confirmation, setConfirmation] = useState("");

  const selected = tasks.data?.find((task) => task.id === selectedId);
  useEffect(() => {
    if (!resourceId && resources[0]) setResourceId(resources[0].id);
  }, [resourceId, resources]);
  useEffect(() => {
    if (isCreating || !tasks.data) return;
    if (selectedId && tasks.data.some((task) => task.id === selectedId)) return;
    setSelectedId(tasks.data[0]?.id);
  }, [isCreating, selectedId, tasks.data]);
  useEffect(() => {
    if (!selected) return;
    setName(selected.name); setInstruction(selected.instruction); setRecurrenceKind(selected.recurrence.kind);
    const interval = intervalParts(selected.recurrence.everySeconds);
    setDueAt(localInput(selected.recurrence.dueAt, selected.timeZone)); setIntervalValue(interval.value); setIntervalUnit(interval.unit);
    setTimeZone(selected.timeZone); setTargetId(selected.target.id); setConfirmation("");
    const resource = resources.find((item) => item.targetKind === selected.target.kind);
    if (resource) setResourceId(resource.id);
  }, [resources, selected?.id, selected?.revision]);

  const resource = resources.find((item) => item.id === resourceId);
  const targetList = useQuery({
    queryKey: ["schedule", "targets", spaceId, resource?.id, resource?.pickerOperationId],
    queryFn: () => client.invoke(spaceId, resource!.pickerOperationId, {}, false),
    enabled: Boolean(resource),
  });
  const targetOptions = useMemo(() => {
    if (!resource) return [] as { id: string; title: string }[];
    const collection = object(targetList.data?.output).targets;
    if (!Array.isArray(collection)) return [];
    return collection.flatMap((raw) => {
      const item = object(raw); const id = stringValue(item.id);
      if (!id) return [];
      return [{ id, title: stringValue(item.title) ?? id }];
    });
  }, [resource, targetList.data]);
  useEffect(() => {
    if (!targetId && targetOptions[0]) setTargetId(targetOptions[0].id);
  }, [targetId, targetOptions]);

  const history = useQuery({
    queryKey: ["schedule", "history", spaceId, selectedId],
    queryFn: async () => asFires((await client.invoke(spaceId, operation("historyOperation"), selectedId ? { taskId: selectedId } : {}, false)).output),
    enabled: Boolean(selectedId),
    refetchInterval: 2_000,
  });
  const invalidate = async () => Promise.all([
    queryClient.invalidateQueries({ queryKey: ["schedule", "tasks", spaceId, moduleId] }),
    queryClient.invalidateQueries({ queryKey: ["schedule", "history", spaceId] }),
  ]);
  const spec = () => {
    if (!resource || !targetId) throw new Error("Choose one registered target.");
    if (!name.trim() || !instruction.trim() || !dueAt || !timeZone.trim()) throw new Error("Name, target, instruction, due time, and time zone are required.");
    const everySeconds = Math.round(Number(intervalValue) * intervalSeconds[intervalUnit]);
    if (recurrenceKind === "interval" && (!Number.isFinite(everySeconds) || everySeconds < 60)) throw new Error("Interval must be at least one minute.");
    return {
      name: name.trim(), target: { kind: resource.targetKind, id: targetId }, action: "snowie" as const,
      instruction: instruction.trim(), recurrence: { kind: recurrenceKind, dueAt: zonedISOString(dueAt, timeZone.trim()), ...(recurrenceKind === "interval" ? { everySeconds } : {}) }, timeZone: timeZone.trim(),
    };
  };
  const save = useMutation({
    mutationFn: () => selected
      ? client.invoke(spaceId, operation("updateOperation"), { taskId: selected.id, expectedRevision: selected.revision, ...spec() }, true, crypto.randomUUID())
      : client.invoke(spaceId, operation("createOperation"), spec(), true, crypto.randomUUID()),
    onSuccess: async (result) => { const task = result.output as Task; setIsCreating(false); setSelectedId(task.id); setMobilePane("editor"); await invalidate(); },
  });
  const stateChange = useMutation({
    mutationFn: ({ task, action }: { task: Task; action: "pause" | "resume" }) => client.invoke(spaceId, operation(`${action}Operation`), { taskId: task.id, expectedRevision: task.revision }, true, crypto.randomUUID()),
    onSuccess: invalidate,
  });
  const runNow = useMutation({
    mutationFn: (task: Task) => client.invoke(spaceId, operation("runOperation"), { taskId: task.id, expectedRevision: task.revision }, true, crypto.randomUUID()),
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: (task: Task) => client.invoke(spaceId, operation("deleteOperation"), { taskId: task.id, expectedRevision: task.revision, confirmation }, true, crypto.randomUUID()),
    onSuccess: async () => { setIsCreating(false); setSelectedId(undefined); setMobilePane("list"); setConfirmation(""); await invalidate(); },
  });
  const editError = save.error ?? remove.error ?? tasks.error ?? targetList.error;
  const statusError = stateChange.error ?? runNow.error ?? history.error;
  const operationError = editError ?? statusError;
  const activeDetails = operationError instanceof ProtocolError && operationError.code === "SCHEDULE_ACTIVE" ? object(operationError.details) : {};
  const activeJobId = stringValue(activeDetails.jobId);
  const activeConversationId = stringValue(activeDetails.conversationId);
  const selectedInterval = intervalParts(selected?.recurrence.everySeconds);
  const selectedRecurrence = selected?.recurrence.kind === "interval"
    ? repeatLabel(selectedInterval.value, selectedInterval.unit)
    : "One time";
  const newRoutine = () => {
    const zone = browserTimeZone();
    setIsCreating(true); setSelectedId(undefined); setName(""); setInstruction(""); setTargetId(targetOptions[0]?.id ?? ""); setRecurrenceKind("once");
    setTimeZone(zone); setDueAt(localInput(new Date(Date.now() + 3_600_000).toISOString(), zone)); setIntervalValue("1"); setIntervalUnit("hour"); setConfirmation(""); setMobilePane("editor");
  };

  return <div className={ui("schedule-layout", `schedule-layout--${mobilePane}`)}>
    <nav className={ui("schedule-list")} aria-label="Scheduled routines" data-scroll-owner="schedule-list">
      <header><div><span className={ui("ss-eyebrow")}>Kernel-owned execution</span><h1>{title.toLowerCase() === "schedule" ? "Automation" : title}</h1></div><Button variant="secondary" onPress={newRoutine}>New</Button></header>
      <div className={ui("resource-items")}>{tasks.data?.map((task) => <Button key={task.id} className={ui("resource-item", task.id === selectedId && "resource-item--selected")} variant="quiet" onPress={() => { setIsCreating(false); setSelectedId(task.id); setMobilePane("editor"); }}><span>{task.name}</span><small>{task.target.kind} · {task.target.id}</small><Status tone={task.state === "enabled" ? "positive" : "neutral"}>{task.state}</Status></Button>)}</div>
      {!tasks.isLoading && !tasks.data?.length ? <EmptyState title="No routines yet">Create one exact target-bound routine. Due work is admitted by the kernel and linked to a fresh Snowie Job.</EmptyState> : null}
    </nav>

    <main className={ui("schedule-editor")} aria-label="Routine workspace">
      <section className={ui("schedule-definition")} aria-label="Routine detail and edit" data-scroll-owner="schedule-editor">
        <header className={ui("schedule-pane-heading")}><Button className={ui("schedule-mobile-only")} icon="arrow-left" variant="quiet" onPress={() => setMobilePane("list")}>Automation</Button><div><span className={ui("ss-eyebrow")}>{selected ? `Revision ${selected.revision}` : "New routine"}</span><h2>{selected?.name ?? "Register a routine"}</h2></div></header>
        <div className={ui("schedule-form")}>
          <div className={ui("schedule-section-heading")}><Icon name="edit" /><div><strong>Definition and trigger</strong><small>Edit the exact target, instruction, recurrence, and next trigger.</small></div></div>
          <Field label="Routine name" value={name} onChange={setName} placeholder="Weekly project review" />
          <SelectField label="Target source" value={resourceId} options={resources.map((item) => ({ id: item.id, label: `${item.title} · ${item.targetKind}` }))} onChange={(value) => { setResourceId(value); setTargetId(""); }} />
          <SelectField label="Exact registered target" value={targetId} options={targetOptions.map((item) => ({ id: item.id, label: `${item.title} · ${item.id}` }))} onChange={setTargetId} />
          {!resources.length && !bootstrap.isLoading ? <p className={ui("state-banner state-banner--error")}>No registered resource module exposes a Schedule-compatible target picker.</p> : null}
          <Field label="Instruction for fresh Snowie Job" value={instruction} onChange={setInstruction} textarea placeholder="Review this exact target and prepare…" />
          <div className={ui("schedule-form-grid")}><SelectField label="Schedule" value={recurrenceKind} options={[{ id: "once", label: "Run once" }, { id: "interval", label: "Repeat" }]} onChange={(value) => setRecurrenceKind(value as typeof recurrenceKind)} /><Field label={recurrenceKind === "interval" ? "First run" : "Run at"} value={dueAt} onChange={setDueAt} type="datetime-local" /></div>
          {recurrenceKind === "interval" ? <><div className={ui("schedule-interval-grid")}><Field label="Repeat every" value={intervalValue} onChange={setIntervalValue} type="number" /><SelectField label="Unit" value={intervalUnit} options={[{ id: "minute", label: "Minutes" }, { id: "hour", label: "Hours" }, { id: "day", label: "Days" }, { id: "week", label: "Weeks" }]} onChange={(value) => setIntervalUnit(value as IntervalUnit)} /></div><div className={ui("schedule-recurrence-summary")}><Icon name="schedule" /><span><strong>{repeatLabel(intervalValue, intervalUnit)}</strong><small>Beginning {dueAt ? dueAt.replace("T", " ") : "when selected"} in {timeZone || "the selected time zone"}. Missed occurrences coalesce into one fire.</small></span></div></> : null}
          <Field label="IANA time zone" value={timeZone} onChange={setTimeZone} placeholder="Asia/Shanghai" />
          <div className={ui("action-row")}><Button isDisabled={save.isPending || !resources.length || !targetId} onPress={() => save.mutate()}>{selected ? "Save revision" : "Create routine"}</Button></div>
          {selected ? <div className={ui("danger-panel")}><strong>Delete {selected.name}</strong><p>Deletes the idle definition and fire history. Its independent Snowie Job ledger remains durable.</p><Field label={`Type ${selected.name} exactly`} value={confirmation} onChange={setConfirmation} /><Button variant="secondary" isDisabled={confirmation !== selected.name || remove.isPending} onPress={() => remove.mutate(selected)}>Delete exact routine</Button></div> : null}
          <OperationError error={editError} />
        </div>
      </section>

      <aside className={ui("schedule-inspector")} aria-label="Routine status and execution history" data-scroll-owner="schedule-history">
        {selected ? <Card className={ui("schedule-status")} eyebrow="Routine control" title="Status" action={<Status tone={selected.state === "enabled" ? "positive" : "neutral"}>{selected.state}</Status>}>
          <div className={ui("schedule-next-due")}><span>{selected.state === "enabled" ? "Next due" : "Paused"}</span><strong>{dueLabel(selected.nextDueAt)}</strong><small>Run now starts a fresh Job without shifting this due time.</small></div>
          <dl className={ui("schedule-status-facts")}>
            <div><dt>Recurrence</dt><dd>{selectedRecurrence}</dd></div>
            <div><dt>Time zone</dt><dd>{selected.timeZone}</dd></div>
            <div><dt>Target</dt><dd>{selected.target.kind} · {selected.target.id}</dd></div>
          </dl>
          <div className={ui("action-row", "schedule-status-actions")}><Button icon="play" isDisabled={runNow.isPending} onPress={() => runNow.mutate(selected)}>Run now</Button><Button variant="secondary" isDisabled={stateChange.isPending} onPress={() => stateChange.mutate({ task: selected, action: selected.state === "enabled" ? "pause" : "resume" })}>{selected.state === "enabled" ? "Pause" : "Resume"}</Button></div>
          {activeJobId && activeConversationId ? <p className={ui("schedule-active-job")}><Link to="/spaces/$spaceId/poke" params={{ spaceId }} search={{ targetKind: selected.target.kind, targetId: selected.target.id, conversationId: activeConversationId, jobId: activeJobId }}>Open active Job {activeJobId}</Link></p> : null}
          <OperationError error={statusError} />
        </Card> : <EmptyState icon="schedule" title="Status after save">Create the routine to see its durable state, next trigger, controls, and execution receipts here.</EmptyState>}

        <section className={ui("schedule-history")} aria-label="Routine execution history">
          <header className={ui("schedule-pane-heading")}><div><span className={ui("ss-eyebrow")}>Durable receipts</span><h2>Execution history</h2></div></header>
          <div className={ui("schedule-history-list")}>{history.data?.map((fire) => <article key={fire.id} className={ui("schedule-fire")}><header><Status tone={fire.state === "failed" ? "warning" : fire.jobState === "succeeded" ? "positive" : "neutral"}>{fire.jobState ?? fire.state}</Status><time dateTime={fire.dueAt}>{new Date(fire.dueAt).toLocaleString()}</time></header><strong>{fire.triggerKind === "manual" ? "Manual trigger" : "Scheduled trigger"}</strong><code>{fire.id}</code>{fire.coalescedOccurrences ? <p>{fire.coalescedOccurrences} missed occurrence{fire.coalescedOccurrences === 1 ? "" : "s"} coalesced.</p> : null}{fire.diagnosticCode ? <p className={ui("error-text")}>{fire.diagnosticCode}</p> : null}{fire.jobId && fire.conversationId && selected ? <Link to="/spaces/$spaceId/poke" params={{ spaceId }} search={{ targetKind: selected.target.kind, targetId: selected.target.id, conversationId: fire.conversationId, jobId: fire.jobId }}>Open Snowie Job</Link> : null}</article>)}</div>
          {selected && !history.isLoading && !history.data?.length ? <EmptyState title="No executions yet">Run this routine now or wait for its next trigger. Each fire links one fresh Snowie Job.</EmptyState> : null}
        </section>
      </aside>
    </main>
  </div>;
}
